import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Clock, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { apiClient } from '../lib/api';

// Easter egg SVG component
const EasterEgg = ({ className, color1 = "#FF9F1C", color2 = "#FFD700" }) => (
  <svg className={className} viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="12" cy="18" rx="10" ry="13" fill={color1} />
    <ellipse cx="12" cy="18" rx="10" ry="13" fill="url(#eggGradient)" />
    <path d="M4 14c4-2 8 2 12 0s4-4 4-4" stroke={color2} strokeWidth="2" strokeLinecap="round" />
    <path d="M4 20c4 2 8-2 12 0s4 4 4 4" stroke={color2} strokeWidth="2" strokeLinecap="round" />
    <circle cx="8" cy="17" r="1.5" fill={color2} />
    <circle cx="16" cy="17" r="1.5" fill={color2} />
    <circle cx="12" cy="22" r="1" fill={color2} />
    <defs>
      <linearGradient id="eggGradient" x1="12" y1="5" x2="12" y2="31" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" stopOpacity="0.3" />
        <stop offset="1" stopColor="white" stopOpacity="0" />
      </linearGradient>
    </defs>
  </svg>
);

// Countdown timer hook
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));

  function calculateTimeLeft(target) {
    const difference = new Date(target) - new Date();
    
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      expired: false
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

// Countdown display component
const CountdownUnit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[60px]">
      <span className="text-2xl sm:text-3xl font-bold text-white tabular-nums">
        {String(value).padStart(2, '0')}
      </span>
    </div>
    <span className="text-xs text-white/80 mt-1 uppercase tracking-wide">{label}</span>
  </div>
);

export const EasterSaleBanner = ({ variant = 'full' }) => {
  const [saleData, setSaleData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.getSaleStatus()
      .then(res => setSaleData(res.data))
      .catch(() => setSaleData(null))
      .finally(() => setLoading(false));
  }, []);

  const timeLeft = useCountdown(saleData?.sale_end_utc);

  // Don't render if sale is not active or loading
  if (loading || !saleData?.sale_active || timeLeft.expired) {
    return null;
  }

  // Compact variant for homepage hero
  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 rounded-xl p-4 mb-6 relative overflow-hidden"
      >
        {/* Decorative eggs */}
        <div className="absolute -left-2 -top-2 opacity-20">
          <EasterEgg className="w-12 h-16" color1="#fff" color2="#FFD700" />
        </div>
        <div className="absolute -right-2 -bottom-2 opacity-20 rotate-12">
          <EasterEgg className="w-10 h-14" color1="#fff" color2="#FF9F1C" />
        </div>
        
        <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-sm sm:text-base">
                🐣 Easter Sale - FREE Guide with Annual Plan!
              </p>
              <p className="text-white/80 text-xs">
                Save £19.99 - Ends Monday
              </p>
            </div>
          </div>
          <Link to="/pricing">
            <Button size="sm" className="bg-white text-emerald-600 hover:bg-white/90 font-semibold">
              Get Offer
            </Button>
          </Link>
        </div>
      </motion.div>
    );
  }

  // Full banner for homepage
  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-4 left-[10%] opacity-30"
        >
          <EasterEgg className="w-16 h-20" color1="#FFD700" color2="#FFF" />
        </motion.div>
        <motion.div
          animate={{ y: [0, 10, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          className="absolute bottom-4 right-[15%] opacity-30"
        >
          <EasterEgg className="w-12 h-16" color1="#FF9F1C" color2="#FFF" />
        </motion.div>
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute top-1/2 right-[5%] opacity-20"
        >
          <EasterEgg className="w-10 h-14" color1="#FFF" color2="#FFD700" />
        </motion.div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
          className="absolute bottom-1/4 left-[5%] opacity-20"
        >
          <EasterEgg className="w-14 h-18" color1="#FFF" color2="#FF9F1C" />
        </motion.div>
        
        {/* Sparkle effects */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                opacity: [0.3, 1, 0.3],
                scale: [0.8, 1.2, 0.8]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                delay: i * 0.4 
              }}
              className="absolute"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`
              }}
            >
              <Sparkles className="w-4 h-4 text-yellow-300/60" />
            </motion.div>
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="text-center">
          {/* Sale badge */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4"
          >
            <span className="text-2xl">🐣</span>
            <span className="text-white font-semibold text-sm uppercase tracking-wide">
              Easter Special Offer
            </span>
            <span className="text-2xl">🥚</span>
          </motion.div>

          {/* Main headline */}
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
            FREE Psychometric Guide
            <span className="block text-yellow-300">with Annual Membership!</span>
          </h2>

          <p className="text-white/90 text-base sm:text-lg mb-6 max-w-2xl mx-auto">
            Get our comprehensive £19.99 preparation guide absolutely FREE when you purchase annual access. 
            <span className="font-semibold"> Limited time only!</span>
          </p>

          {/* Countdown timer */}
          <div className="mb-6">
            <p className="text-white/80 text-sm mb-3 flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              Offer ends in:
            </p>
            <div className="flex justify-center gap-3 sm:gap-4">
              <CountdownUnit value={timeLeft.days} label="Days" />
              <CountdownUnit value={timeLeft.hours} label="Hours" />
              <CountdownUnit value={timeLeft.minutes} label="Mins" />
              <CountdownUnit value={timeLeft.seconds} label="Secs" />
            </div>
          </div>

          {/* CTA */}
          <Link to="/pricing">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-yellow-50 font-bold text-lg px-8 shadow-lg hover:shadow-xl transition-all"
              data-testid="easter-sale-cta"
            >
              <Gift className="w-5 h-5 mr-2" />
              Claim Your Free Guide
            </Button>
          </Link>

          <p className="text-white/70 text-xs mt-4">
            Annual membership £29.99 + FREE guide worth £19.99 = Save £19.99!
          </p>
        </div>
      </div>
    </motion.section>
  );
};

export default EasterSaleBanner;
